import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Tags,
  MoreHorizontal,
  ClipboardList,
  Settings,
  Crown,
} from 'lucide-react';
import { useState } from 'react';
import { clsx } from 'clsx';

interface NavItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  matchPaths?: string[];
}

const mainNavItems: NavItem[] = [
  { 
    name: 'Dashboard', 
    href: '/dashboard', 
    icon: LayoutDashboard,
  },
  { 
    name: 'Products', 
    href: '/shop/products', 
    icon: Package,
    matchPaths: ['/shop/products'],
  },
  { 
    name: 'Orders', 
    href: '/shop/orders', 
    icon: ClipboardList,
  },
  { 
    name: 'Labels', 
    href: '/labels', 
    icon: Tags,
  },
];

const moreMenuItems: NavItem[] = [
  { name: 'Shop', href: '/shop', icon: ShoppingCart },
  { name: 'Pricing', href: '/pricing', icon: Crown },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);

  // Don't show on auth pages
  if (location.pathname.startsWith('/login') || 
      location.pathname.startsWith('/register') ||
      location.pathname.startsWith('/forgot-password') ||
      location.pathname.startsWith('/reset-password')) {
    return null;
  }

  const isActive = (item: NavItem) => {
    if (item.matchPaths) {
      return item.matchPaths.some(path => location.pathname.startsWith(path));
    }
    return location.pathname === item.href || location.pathname.startsWith(item.href + '/');
  };

  const isMoreActive = moreMenuItems.some(item => isActive(item));

  return (
    <>
      {/* More Menu Overlay */}
      {showMore && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setShowMore(false)}
        />
      )}

      {/* More Menu */}
      {showMore && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-20 right-4 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 lg:hidden min-w-[180px]"
        >
          {moreMenuItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setShowMore(false)}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 transition-colors',
                isActive(item)
                  ? 'bg-green-50 text-[#4A7C2E]'
                  : 'text-slate-600 hover:bg-gray-50'
              )}
            >
              <item.icon size={20} />
              <span className="font-medium">{item.name}</span>
            </NavLink>
          ))}
        </motion.div>
      )}

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-gray-200 safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {mainNavItems.map((item) => {
            const active = isActive(item);
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={clsx(
                  'flex flex-col items-center justify-center flex-1 h-full min-w-[64px] transition-colors relative',
                  active ? 'text-[#4A7C2E]' : 'text-slate-500'
                )}
              >
                {active && (
                  <motion.div
                    layoutId="bottomNavIndicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-[#4A7C2E] rounded-b-full"
                  />
                )}
                <item.icon size={22} strokeWidth={active ? 2.5 : 2} />
                <span className={clsx(
                  'text-[10px] mt-1 font-medium',
                  active && 'font-semibold'
                )}>
                  {item.name}
                </span>
              </NavLink>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setShowMore(!showMore)}
            className={clsx(
              'flex flex-col items-center justify-center flex-1 h-full min-w-[64px] transition-colors relative',
              showMore || isMoreActive ? 'text-[#4A7C2E]' : 'text-slate-500'
            )}
          >
            {(showMore || isMoreActive) && (
              <motion.div
                layoutId="bottomNavIndicator"
                className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-[#4A7C2E] rounded-b-full"
              />
            )}
            <MoreHorizontal size={22} strokeWidth={showMore || isMoreActive ? 2.5 : 2} />
            <span className={clsx(
              'text-[10px] mt-1 font-medium',
              (showMore || isMoreActive) && 'font-semibold'
            )}>
              More
            </span>
          </button>
        </div>
      </nav>
    </>
  );
}
